// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * Resource Manager
 * 
 * Manages ship resources (food, water, oxygen, medical supplies, spare parts)
 * Handles consumption based on crew count and time
 * Provides warnings when resources are low
 */

import STORAGE_KEYS from '../system/STORAGE_KEYS';

class ResourceManager {
  constructor() {
    // Resource storage
    this.resources = {
      food: 100,      // Units
      water: 200,     // Units
      oxygen: 100,    // Units
      medical: 50,    // Units
      parts: 30       // Units
    };

    // Maximum capacity
    this.maxCapacity = {
      food: 200,
      water: 400,
      oxygen: 200,
      medical: 100,
      parts: 100
    };

    // Consumption rates per crew member per day
    this.consumptionRates = {
      food: 1.0,      // 1 unit per crew per day
      water: 2.0,     // 2 units per crew per day
      oxygen: 0.5,    // 0.5 units per crew per day
      medical: 0,     // Only consumed when healing injuries
      parts: 0        // Only consumed during repairs
    };

    // Warning thresholds (days remaining)
    this.warningThresholds = {
      critical: 2,    // 2 days or less
      low: 5          // 5 days or less
    };

    // Rationing mode (reduces consumption)
    this.rationing = false;
    this.rationingMultiplier = 0.5;  // 50% consumption when rationing

    // Load from localStorage
    this.loadFromStorage();
  }

  /**
   * Get current resource amount
   */
  getResource(resourceType) {
    return this.resources[resourceType] || 0;
  }

  /**
   * Get all resources
   */
  getAllResources() {
    return { ...this.resources };
  }

  /**
   * Set resource amount
   */
  setResource(resourceType, amount) {
    if (this.resources.hasOwnProperty(resourceType)) {
      this.resources[resourceType] = Math.max(0, Math.min(amount, this.maxCapacity[resourceType]));
      this.saveToStorage();
      return true;
    }
    return false;
  }

  /**
   * Add resource amount
   */
  addResource(resourceType, amount) {
    if (this.resources.hasOwnProperty(resourceType)) {
      this.resources[resourceType] = Math.min(
        this.resources[resourceType] + amount,
        this.maxCapacity[resourceType]
      );
      this.saveToStorage();
      return true;
    }
    return false;
  }

  /**
   * Remove resource amount
   */
  removeResource(resourceType, amount) {
    if (this.resources.hasOwnProperty(resourceType)) {
      this.resources[resourceType] = Math.max(0, this.resources[resourceType] - amount);
      this.saveToStorage();
      return true;
    }
    return false;
  }

  /**
   * Calculate daily consumption for a resource
   */
  getDailyConsumption(resourceType, crewCount) {
    const baseRate = this.consumptionRates[resourceType] || 0;
    const multiplier = this.rationing ? this.rationingMultiplier : 1.0;
    return crewCount * baseRate * multiplier;
  }

  /**
   * Calculate days remaining for a resource
   */
  getDaysRemaining(resourceType, crewCount) {
    const current = this.resources[resourceType];
    const daily = this.getDailyConsumption(resourceType, crewCount);
    
    if (daily === 0) return Infinity;
    return current / daily;
  }

  /**
   * Get resource status (ok, low, critical, empty)
   */
  getResourceStatus(resourceType, crewCount) {
    const daysRemaining = this.getDaysRemaining(resourceType, crewCount);
    
    if (this.resources[resourceType] <= 0) return 'empty';
    if (daysRemaining <= this.warningThresholds.critical) return 'critical';
    if (daysRemaining <= this.warningThresholds.low) return 'low';
    return 'ok';
  }

  /**
   * Get status color for UI
   */
  getStatusColor(status) {
    switch (status) {
      case 'ok': return '#00ff88';      // Green
      case 'low': return '#ffaa00';     // Orange
      case 'critical': return '#ff4444'; // Red
      case 'empty': return '#ff0000';   // Bright red
      default: return '#00ffff';        // Cyan
    }
  }

  /**
   * Update resources based on time elapsed
   */
  updateResources(deltaTime, crewCount) {
    // Convert deltaTime (seconds) to days
    const SECONDS_PER_DAY = 86400; // 24 hours
    const daysElapsed = deltaTime / SECONDS_PER_DAY;

    // Consume resources
    Object.keys(this.consumptionRates).forEach(resourceType => {
      const consumption = this.getDailyConsumption(resourceType, crewCount) * daysElapsed;
      if (consumption > 0) {
        this.removeResource(resourceType, consumption);
      }
    });

    // Check for critical shortages
    const warnings = this.getWarnings(crewCount);
    return warnings;
  }

  /**
   * Get all resource warnings
   */
  getWarnings(crewCount) {
    const warnings = [];

    Object.keys(this.resources).forEach(resourceType => {
      const status = this.getResourceStatus(resourceType, crewCount);
      const daysRemaining = this.getDaysRemaining(resourceType, crewCount);

      if (status === 'empty') {
        warnings.push({
          type: 'critical',
          resource: resourceType,
          message: `${resourceType.toUpperCase()} DEPLETED - Crew in danger!`,
          daysRemaining: 0
        });
      } else if (status === 'critical') {
        warnings.push({
          type: 'critical',
          resource: resourceType,
          message: `${resourceType.toUpperCase()} CRITICAL - ${Math.floor(daysRemaining)} days remaining`,
          daysRemaining: Math.floor(daysRemaining)
        });
      } else if (status === 'low') {
        warnings.push({
          type: 'warning',
          resource: resourceType,
          message: `${resourceType.toUpperCase()} LOW - ${Math.floor(daysRemaining)} days remaining`,
          daysRemaining: Math.floor(daysRemaining)
        });
      }
    });

    return warnings;
  }

  /**
   * Toggle rationing mode
   */
  toggleRationing() {
    this.rationing = !this.rationing;
    this.saveToStorage();
    return this.rationing;
  }

  /**
   * Get rationing status
   */
  isRationing() {
    return this.rationing;
  }

  /**
   * Resupply resources (at station/planet)
   */
  resupply(resourceType, amount, costPerUnit) {

    const spaceAvailable = this.maxCapacity[resourceType] - this.resources[resourceType];
    const actualAmount = Math.min(amount, spaceAvailable);
    const actualCost = actualAmount * costPerUnit;

    if (actualAmount > 0) {
      this.addResource(resourceType, actualAmount);
      return {
        success: true,
        amount: actualAmount,
        cost: actualCost,
        message: `Purchased ${actualAmount} units of ${resourceType} for ${actualCost} CR`
      };
    }

    return {
      success: false,
      amount: 0,
      cost: 0,
      message: `No space available for ${resourceType}`
    };
  }

  /**
   * Get resource percentage (for UI bars)
   */
  getResourcePercentage(resourceType) {
    const current = this.resources[resourceType];
    const max = this.maxCapacity[resourceType];
    return (current / max) * 100;
  }

  /**
   * Check if any resources are critically low
   */
  hasCriticalShortage(crewCount) {
    return Object.keys(this.resources).some(resourceType => {
      return this.getResourceStatus(resourceType, crewCount) === 'critical' ||
             this.getResourceStatus(resourceType, crewCount) === 'empty';
    });
  }

  /**
   * Get resource costs (base prices at stations)
   */
  getResourceCosts() {
    return {
      food: 5,        // 5 credits per unit
      water: 3,       // 3 credits per unit
      oxygen: 4,      // 4 credits per unit
      medical: 10,    // 10 credits per unit
      parts: 15       // 15 credits per unit
    };
  }

  /**
   * Purchase resources
   * @param {string} resourceType - Type of resource to purchase
   * @param {number} amount - Amount to purchase
   * @param {number} credits - Available credits
   * @param {string} locationType - Type of location for pricing
   * @returns {Object} Result with success, cost, and message
   */
  purchaseResource(resourceType, amount, credits, locationType = 'default') {
    if (!this.resources.hasOwnProperty(resourceType)) {
      return { success: false, message: 'Invalid resource type' };
    }

    const costs = this.getResourceCosts();
    const unitCost = costs[resourceType];
    const totalCost = unitCost * amount;

    // Check if player has enough credits
    if (credits < totalCost) {
      return { 
        success: false, 
        message: `Insufficient credits. Need ${totalCost}, have ${credits}` 
      };
    }

    // Check if would exceed capacity
    const newAmount = this.resources[resourceType] + amount;
    const maxCapacity = this.maxCapacity[resourceType];
    
    if (newAmount > maxCapacity) {
      const canBuy = maxCapacity - this.resources[resourceType];
      return { 
        success: false, 
        message: `Cannot exceed capacity. Can only buy ${canBuy} more units` 
      };
    }

    // Purchase successful
    this.resources[resourceType] += amount;
    this.saveToStorage();

    return {
      success: true,
      cost: totalCost,
      message: `Purchased ${amount} units of ${resourceType} for ${totalCost} credits`
    };
  }

  /**
   * Reset to starting values
   */
  reset() {
    this.resources = {
      food: 100,
      water: 200,
      oxygen: 100,
      medical: 50,
      parts: 30
    };
    this.rationing = false;
    this.saveToStorage();
  }

  /**
   * Save to localStorage
   */
  saveToStorage() {
    try {
      const data = {
        resources: this.resources,
        rationing: this.rationing
      };
      localStorage.setItem(STORAGE_KEYS.RESOURCES, JSON.stringify(data));
    } catch (error) {
      console.error('[ResourceManager] Failed to save to localStorage:', error);
    }
  }

  /**
   * Load from localStorage
   */
  loadFromStorage() {
    try {
      const data = localStorage.getItem(STORAGE_KEYS.RESOURCES);
      if (data) {
        const parsed = JSON.parse(data);
        this.resources = parsed.resources || this.resources;
        this.rationing = parsed.rationing || false;
      }
    } catch (error) {
      console.error('[ResourceManager] Failed to load from localStorage:', error);
    }
  }
}

// Singleton instance
const resourceManager = new ResourceManager();
export default resourceManager;
